import request from '../../utils/request';

export function getOrders(params) {
  return request('/orders', {
    method: 'GET',
    params: params,
  });
}

export function getOrder(id) {
  return request(`/orders/${id}`, {
    method: 'GET',
  });
}

// OVO-252 — reschedule a skipped/failed order to a new date (detaches it from
// the old route, resets to UNASSIGNED, bumps deliveryAttempts).
export function rescheduleOrder(orderCode, data) {
  return request(`/orders/${orderCode}/reschedule`, { method: 'POST', data });
}

// OVO-014 — operator-side edits with audit logging.
export function operatorCreateOrderProduct(orderCode, data) {
  return request(`/orders/${orderCode}/products`, { method: 'POST', data });
}
export function operatorUpdateOrderProduct(orderCode, productId, data) {
  return request(`/orders/${orderCode}/products/${productId}`, { method: 'PATCH', data });
}
export function operatorDeleteOrderProduct(orderCode, productId) {
  return request(`/orders/${orderCode}/products/${productId}`, { method: 'DELETE' });
}
export function operatorPatchOrder(orderCode, data) {
  return request(`/orders/${orderCode}/operator-edit`, { method: 'PATCH', data });
}
export function getOrderAuditLog(orderCode) {
  return request(`/orders/${orderCode}/audit-log`, { method: 'GET' });
}

// OVO-208 — undo the last operator data-edit on the order (single-step).
export function undoLastOperatorEdit(orderCode) {
  return request(`/orders/${orderCode}/undo-last`, { method: 'POST' });
}

// Operator correction of a completed order's actual delivery time. The estimate
// infers it from the route (same idea as the shift-time estimate); the PATCH
// edits the underlying status-log timestamps + audits.
export function getOrderActualTimeEstimate(orderCode) {
  return request(`/orders/${orderCode}/actual-time-estimate`, { method: 'GET' });
}
export function operatorEditOrderActualTimes(orderCode, data) {
  return request(`/orders/${orderCode}/operator-actual-times`, { method: 'PATCH', data });
}

export function createOrder(data) {
  return request('/orders', {
    method: 'POST',
    data,
  });
}

export function changeToUnassigned(data) {
  return request('/orders/change-to-unassigned', {
    method: 'POST',
    data,
  });
}

export function changeToDraft(data) {
  return request('/orders/change-to-draft', {
    method: 'POST',
    data,
  });
}

// "Cancel stop" (Seb, Aug 2026) — detach + terminal CANCELLED status; the
// order does not return to the unscheduled pool.
export function changeToCancelled(params) {
  return request('/orders/change-to-cancelled', {
    method: 'POST',
    data: params,
  });
}

// OVO-116 — restore a soft-deleted order from the trash (admin recovery).
export function restoreOrder(orderCode) {
  return request(`/orders/${orderCode}/restore`, {
    method: 'POST',
  });
}

// OVO-116 — PERMANENT delete (real DB removal) of trashed orders. Irreversible.
export function permanentDeleteOrders(orderCodes) {
  return request('/orders/permanent-delete', {
    method: 'POST',
    data: { orderCodes },
  });
}

// OVO-116 — empty the trash entirely (permanently delete every trashed order).
export function emptyTrash() {
  return request('/orders/empty-trash', {
    method: 'POST',
  });
}

export function updateOrder(data) {
  const id = data.id;
  delete data.id;
  return request(`/orders/${id}`, {
    method: 'PUT',
    data,
  });
}

export function uploadOrders(data) {
  return request(`/orders/upload`, {
    method: 'POST',
    data,
  });
}

export function deleteOrders(data) {
  return request(`/orders/bulk-delete`, {
    method: 'POST',
    data,
  });
}

// ─── OVO-103 — per-step order media ──────────────────────────────────────

export function listOrderMedia(orderCode) {
  return request(`/orders/${orderCode}/media`, { method: 'GET' });
}

export function addOrderMedia(orderCode, { fileId, stepType }) {
  return request(`/orders/${orderCode}/media`, {
    method: 'POST',
    data: { fileId, stepType },
  });
}

export function removeOrderMedia(orderCode, fileOrderId) {
  return request(`/orders/${orderCode}/media/${fileOrderId}`, {
    method: 'DELETE',
  });
}

// Reuses the shared upload pipeline (`POST /common/shared-upload`). Returns
// `{ id, url, key }` — the caller links the returned file id to the order
// via `addOrderMedia` with the chosen step type.
export function uploadSharedFile(file) {
  const form = new FormData();
  form.append('file', file);
  return request(`/common/shared-upload`, {
    method: 'POST',
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}
